/**
 * Deep inspection for basket meta.
 */

import type { RootState } from '/business/shared/state/store';
import type { BasketMeta } from '/business/shared/state/reducers/basket';

type KnownBasketMetaPathValue = {
  aspirationPercentageTarget: NonNullable<BasketMeta['aspirationPercentageTarget']>;
  initialReductionPercentage: number;
  projections: NonNullable<BasketMeta['projections']>;
};

type BasketMetaLookupValue = BasketMeta | BasketMeta[keyof BasketMeta] | number | null | undefined;

const isRecord = (value: BasketMetaLookupValue): value is Record<string, BasketMetaLookupValue> =>
  value !== null && typeof value === 'object';

const getItemFromString = (obj: BasketMeta | undefined, str: string) =>
  str.split('.').reduce<BasketMetaLookupValue>((value, key) => {
    if (!isRecord(value)) {
      return undefined;
    }
    return (value as Record<string, BasketMetaLookupValue>)[key];
  }, obj);

function getBasketMetaDeepInspectionSelector<TKey extends keyof KnownBasketMetaPathValue, TDefault = null>(
  state: RootState,
  item: TKey,
  defaultValue?: TDefault,
): KnownBasketMetaPathValue[TKey] | TDefault;
function getBasketMetaDeepInspectionSelector<TDefault = null>(
  state: RootState,
  item?: string | null,
  defaultValue?: TDefault,
): BasketMetaLookupValue | TDefault;
function getBasketMetaDeepInspectionSelector<TDefault = null>(
  state: RootState,
  item: string | null = null,
  defaultValue: TDefault = null as TDefault,
) {
  if (!item) {
    return defaultValue;
  }
  return getItemFromString(state.basket?.meta, item) || defaultValue;
}

export default getBasketMetaDeepInspectionSelector;
